<template>
  <div class="c-collaborate-opportunities-editor__container">
    <BittsDropdown
      v-click-away="informStopEditing"
      prompt="Select Opportunity"
      :transparent="true"
      :options="opportunityOptions"
      :is-initially-visible="true"
      parent-class="c-collaborate-opportunities-editor__dropdown"
      placement="bottomLeft"
      @visibility-changed="informStopEditing"
    >
      <template #content>
        <BittsLoading :is-loading="loading">
          <div>
            <p class="text-neutral-text py-4 px-8"> Select Opportunity </p>
            <div
              v-for="(opt, idx) in opportunityOptions"
              :key="`opportunity-${opt.id}-${idx}`"
              class="c-collaborate-opportunities-editor__option"
              role="presentation"
              @click="onOptionSelected(opt)"
              @mouseover="showTooltip[opt.id || ''] = true"
              @mouseleave="showTooltip[opt.id || ''] = false"
            >
              <OpportunityTooltip
                :opt="opt"
                :show-tooltip="showTooltip[opt.id || '']"
                placement="right"
                overlay-class="c-collaborate-opportunities-editor__tooltip"
                @tooltip-cta-clicked="onCtaClicked(opt)"
              >
                <button
                  type="button"
                  class="block w-full truncate text-left cursor-pointer"
                >
                  {{ opt.name || '--' }}
                </button>
              </OpportunityTooltip>
            </div>
          </div>
        </BittsLoading>
      </template>
    </BittsDropdown>
  </div>
</template>
<script lang="ts">
import { BittsDropdown, BittsLoading } from '@crossbeam/bitts';
import { Nullable } from '@crossbeam/types';

import { Column, ICellRendererParams } from '@ag-grid-community/core';
import { onMounted, reactive, ref } from 'vue';
import { useRoute } from 'vue-router';
import { mixin as VueClickAway } from 'vue3-click-away';

import OpportunityTooltip from '@/components/shared-list/OpportunityTooltip.vue';

import { crossbeamApi } from '@/api';
import { captureException } from '@/errors';
import { useFlashesStore } from '@/stores';
import {
  SharedListDataColumnValue,
  SharedListOpportunity,
} from '@/types/shared_lists';

type Props = { params: ICellRendererParams };

export default {
  name: 'SharedListOpportunitiesEditorCell',
  components: {
    BittsDropdown,
    BittsLoading,
    OpportunityTooltip,
  },
  props: {
    params: {
      default: null,
      type: Object,
    },
  },
  mixins: [VueClickAway],
  setup(props: Props) {
    const selectedOption = ref<Nullable<SharedListOpportunity | string>>(null);
    const loading = ref(true);
    const opportunityOptions = ref<SharedListOpportunity[]>([]);
    const showTooltip = reactive<Record<string, boolean>>({});
    const flashesStore = useFlashesStore();
    const route = useRoute();
    const newData = [...props.params.data.data];
    function onCtaClicked(opt: SharedListOpportunity) {
      if (!opt.crm_record_link) return;
      window.open(opt.crm_record_link, '_blank');
    }

    function informStopEditing() {
      props.params.api?.stopEditing();
    }

    function findColumnSetNewValue(
      columnName: string,
      value: SharedListDataColumnValue,
    ) {
      const allColumns = props.params.api?.getColumns() || [];
      const foundCol = allColumns.find(
        (col: Column) => col.getColDef().headerName === columnName,
      );
      const foundColIdx = newData.findIndex(
        (col) => col.column_id === foundCol?.getColId(),
      );
      newData.splice(foundColIdx, 1, {
        column_id: foundCol?.getColId(),
        value,
      });
    }

    function onOptionSelected(option: SharedListOpportunity) {
      const {
        configured_amount: amount,
        sales_stage: salesStage,
        id: opportunityId,
        name = '--',
      } = option;

      findColumnSetNewValue('Opportunity Stage', salesStage || '');
      findColumnSetNewValue('Opportunity Amount', amount?.toString() || '');
      findColumnSetNewValue('Opportunity Name', name || '');

      props.params.node.updateData({
        ...props.params.data,
        opportunity: option,
        data: [...newData],
      });
      selectedOption.value = name;
      updateOpportunity(opportunityId || '');
      props.params.api?.redrawRows({ rowNodes: [props.params.node] });
      informStopEditing();
    }

    function getValue() {
      return selectedOption.value;
    }

    async function updateOpportunity(id: string) {
      const listId = route.params.id as string;
      const {
        source_id: sourceId,
        record_id: recordId,
        row_id: rowId,
      } = props.params.data;
      const body = {
        source_id: sourceId,
        master_id: recordId,
        opportunity_id: id,
      };
      try {
        await crossbeamApi.PUT(
          '/v0.1/lists/{list_id}/rows/{row_id}/opportunity',
          {
            params: {
              path: {
                list_id: listId,
                row_id: rowId,
              },
            },
            body,
          },
        );
      } catch (e) {
        captureException(e);
        flashesStore.addErrorFlash({
          message:
            'Something went wrong, reach out to our support team for help',
        });
      }
    }

    onMounted(async () => {
      loading.value = true;
      const { source_id: sourceId, record_id: recordId } =
        props.params?.data || {};
      const { data, error } = await crossbeamApi.GET(
        '/v0.1/records/{source_id}/{source_primary_key}/opportunities',
        {
          params: {
            path: {
              source_id: sourceId,
              source_primary_key: recordId,
            },
          },
        },
      );
      if (error) {
        captureException(error);
        flashesStore.addErrorFlash({
          message:
            'Something went wrong, reach out to our support team for help',
        });
        loading.value = false;
        return;
      }
      opportunityOptions.value = data.items;
      if (!data.items.length) {
        selectedOption.value = 'No opportunity available';
        informStopEditing();
      }
      loading.value = false;
    });
    return {
      getValue,
      informStopEditing,
      showTooltip,
      opportunityOptions,
      loading,
      onOptionSelected,
      onCtaClicked,
    };
  },
};
</script>
<style lang="pcss">
@reference "../../css/theme.css";
.c-collaborate-opportunities-editor__container {
  @apply max-w-full w-full;
  .c-bitts-dropdown,
  .c-bitts-dropdown__prompt {
    @apply w-full;
  }
  .c-bitts-dropdown
    > .ant-popover-open.c-bitts-dropdown__prompt
    > .c-bitts-dropdown__prompt--basic {
    @apply px-0 text-neutral-text-placeholder w-full flex items-center justify-between;
    svg {
      color: theme(colors.neutral-accent) !important;
    }
  }
}
.c-collaborate-opportunities-editor__dropdown {
  @apply w-[310px];
  &.c-bitts-dropdown-content
    .c-bitts-dropdown-selections
    .c-bitts-dropdown-selections__option {
    @apply mx-4;
  }
}
.c-collaborate-opportunities-editor__option {
  @apply px-8 py-4 mx-4 mb-4;
  &:hover,
  &:focus-visible {
    @apply bg-neutral-100 cursor-pointer rounded-bts-sm;
  }
}
.c-collaborate-opportunities-editor__tooltip {
  @apply max-w-[310px];
  left: 312px !important;
  .ant-tooltip-content {
    margin-bottom: 64px;
    @apply w-[240px];
  }
}
</style>
